import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Stack, Card, Group, Text, Title, Button } from '@repletoryreach/sdk-ui';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { admin, isApiError } from '@repletoryreach/sdk-api';
import { useT } from '../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';

/**
 * Who production alerts go to (owner's decision of 2 October 2026): every alert by email, critical
 * ones also by WhatsApp from the platform's own number with an approved template. The settings
 * service's router reads these on every alert, so a change applies to the next one.
 */
export function RecipientsCard() {
  const t = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'alert-recipients'], queryFn: () => admin.getAlertRecipients() });
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [template, setTemplate] = useState('');
  const [language, setLanguage] = useState('');
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    const d = q.data?.data;
    if (!d || seeded) return;
    setEmail(d.email.join(', '));
    setWhatsapp(d.whatsapp ?? '');
    setTemplate(d.template);
    setLanguage(d.language);
    setSeeded(true);
  }, [q.data, seeded]);

  const [fieldError, setFieldError] = useState<{ email?: string; whatsapp?: string; template?: string }>({});
  const save = useMutation({
    mutationFn: () => admin.updateAlertRecipients({ email, whatsapp, template, language }),
    onMutate: () => setFieldError({}),
    onSuccess: (res) => {
      qc.setQueryData(['admin', 'alert-recipients'], res);
      setSeeded(false);
      notifySuccess(t('system.alerts.recipients.saved'), t('system.alerts.recipients.title'));
    },
    onError: (e) => {
      const code = isApiError(e) ? e.code : '';
      if (code === 'INVALID_EMAIL') return setFieldError({ email: t('system.alerts.recipients.badEmail') });
      if (code === 'INVALID_NUMBER') return setFieldError({ whatsapp: t('system.alerts.recipients.badNumber') });
      if (code === 'INVALID_TEMPLATE') return setFieldError({ template: t('system.alerts.recipients.badTemplate') });
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('system.alerts.recipients.title'));
    }
  });

  return (
    <Card withBorder>
      <Stack gap="md">
        <Stack gap="xs">
          <Title order={4}>{t('system.alerts.recipients.title')}</Title>
          <Text role="bodySmall" c={MUTED}>{t('system.alerts.recipients.body')}</Text>
        </Stack>
        <TextInput
          label={t('system.alerts.recipients.email')}
          description={t('system.alerts.recipients.emailHint')}
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          error={fieldError.email}
          placeholder="ops@repletoryreach.co.za"
        />
        <TextInput
          label={t('system.alerts.recipients.whatsapp')}
          description={t('system.alerts.recipients.whatsappHint')}
          value={whatsapp}
          onChange={(e) => setWhatsapp(e.currentTarget.value)}
          error={fieldError.whatsapp}
          placeholder="+27 82 000 0000"
        />
        <Group grow align="flex-start">
          <TextInput
            label={t('system.alerts.recipients.template')}
            description={t('system.alerts.recipients.templateHint')}
            value={template}
            onChange={(e) => setTemplate(e.currentTarget.value)}
            error={fieldError.template}
          />
          <TextInput
            label={t('system.alerts.recipients.language')}
            description={t('system.alerts.recipients.languageHint')}
            value={language}
            onChange={(e) => setLanguage(e.currentTarget.value)}
          />
        </Group>
        <Group justify="flex-end">
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={q.isPending}>{t('action.save')}</Button>
        </Group>
      </Stack>
    </Card>
  );
}
